import {
  capitalize,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
  CardHeader,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ReactNode } from "react";
import { formatPhoneNumber, getCalcTitleFragments } from "utils";
import { useTranslation } from "react-i18next";
import { Logo, Contact } from "types";
import {
  AlternateEmail,
  ArrowDropDown,
  Business,
  Phone,
} from "@mui/icons-material";
import { AUTURA_ASPHALT_LIGHT, AUTURA_AZURE } from "context/themeContext";

interface PermitPortalPreviewProps {
  logo: Logo;
  publicContactInfo: Contact;
  entityName: string;
}

export const PermitPortalPreview = ({
  logo,
  publicContactInfo,
  entityName,
}: PermitPortalPreviewProps): ReactNode => {
  const { palette } = useTheme();
  const { t } = useTranslation();

  return (
    <Container component="aside" maxWidth="xs" sx={{ pb: "50px" }}>
      <Card
        sx={{
          backgroundColor: palette.primary.main,
          display: "flex",
          flexDirection: "column",
          borderRadius: "0px",
        }}
        raised
        elevation={3}
        component={"section"}
      >
        {logo && (
          <img
            src={logo.url}
            style={{
              objectFit: "contain",
              padding: logo.padding || 0,
            }}
            height={120}
            alt={"logo banner"}
            id={"logo-banner"}
          />
        )}
        {!logo && (
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 0, md: 1 }}
            id={"logo-banner"}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 120,
              width: "100%",
              padding: "0 16px",
            }}
          >
            {getCalcTitleFragments(entityName).map((piece, index) => (
              <Typography
                variant={"h4"}
                color={palette.primary.contrastText}
                noWrap
                key={index}
              >
                {piece}
              </Typography>
            ))}
          </Stack>
        )}
      </Card>
      <Card
        sx={{
          minHeight: "50vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "#FFF",
          borderRadius: "0px",
          border: `1px solid ${AUTURA_ASPHALT_LIGHT}`,
          px: 1,
          py: 4,
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Grid container width={"100%"} spacing={2}>
            <Grid size={12}>
              <Typography variant={"h6"} textTransform={"capitalize"}>
                {t("preview.permit_portal.your_information", {
                  ns: "properties",
                })}
              </Typography>
            </Grid>
            <Grid size={12}>
              <TextField
                label={`${t("permit.contact.name", { ns: "entities" })} *`}
                name={"name"}
                fullWidth
                variant={"outlined"}
                margin={"none"}
                InputLabelProps={{
                  sx: {
                    textTransform: "capitalize",
                  },
                }}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: {
                    cursor: "default",
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name={"phone"}
                label={t("permit.contact.primaryPhone", { ns: "entities" })}
                fullWidth
                variant={"outlined"}
                margin={"none"}
                InputLabelProps={{
                  sx: {
                    textTransform: "capitalize",
                  },
                }}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: {
                    cursor: "default",
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label={`${t("permit.contact.primaryEmail", {
                  ns: "entities",
                })} *`}
                name={"email"}
                fullWidth
                variant={"outlined"}
                margin={"none"}
                InputLabelProps={{
                  sx: {
                    textTransform: "capitalize",
                  },
                }}
                InputProps={{
                  readOnly: true,
                }}
                inputProps={{
                  style: {
                    cursor: "default",
                  },
                }}
              />
            </Grid>
            <Grid size={12}>
              <Stack direction={"row"} spacing={2}>
                <TextField
                  name={"plateNumber"}
                  label={t("vehicle.plate.number", { ns: "entities" })}
                  fullWidth
                  variant={"outlined"}
                  margin={"none"}
                  InputLabelProps={{
                    sx: {
                      textTransform: "capitalize",
                    },
                  }}
                  InputProps={{
                    readOnly: true,
                  }}
                  inputProps={{
                    style: {
                      cursor: "default",
                    },
                  }}
                />
                <TextField
                  name={"plateState"}
                  label={t("vehicle.plate.state", { ns: "entities" })}
                  fullWidth
                  variant={"outlined"}
                  margin={"none"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ backgroundColor: "white" }}
                      >
                        <ArrowDropDown />
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack direction={"row"} spacing={2}>
                <TextField
                  name={"make"}
                  label={t("vehicle.make", { ns: "entities" })}
                  fullWidth
                  variant={"outlined"}
                  margin={"none"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ backgroundColor: "white" }}
                      >
                        <ArrowDropDown />
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                />
                <TextField
                  name={"model"}
                  label={t("vehicle.model", { ns: "entities" })}
                  fullWidth
                  variant={"outlined"}
                  margin={"none"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        sx={{ backgroundColor: "white" }}
                      >
                        <ArrowDropDown />
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={6}>
              <TextField
                name={"color"}
                label={t("vehicle.color", { ns: "entities" })}
                fullWidth
                variant={"outlined"}
                margin={"none"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{ backgroundColor: "white" }}
                    >
                      <ArrowDropDown />
                    </InputAdornment>
                  ),
                  readOnly: true,
                }}
              />
            </Grid>
            <Grid size={12} />
            <Grid size={12}>
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                width={"100%"}
                display={"flex"}
              >
                <Button
                  variant={"contained"}
                  color="primary"
                  sx={{ height: 40, borderRadius: "100px" }}
                >
                  <Typography
                    variant="button"
                    textTransform={"capitalize"}
                    color={palette.primary.contrastText}
                  >
                    {t("preview.permit_portal.button", { ns: "properties" })}
                  </Typography>
                </Button>
              </Stack>
            </Grid>
            <Grid size={12} />
          </Grid>
        </CardContent>
        <Typography
          variant={"caption"}
          width={"100%"}
          textAlign={"center"}
          display={"block"}
          flex={1}
        >
          {`${capitalize(
            t("preview.permit_portal.tandc_1", { ns: "properties" }),
          )} `}
          <Button variant={"text"} sx={{ p: 0, m: 0 }}>
            <Typography
              variant={"caption"}
              color={AUTURA_AZURE}
              textTransform={"lowercase"}
              sx={{ textDecoration: "underline", pb: "1px", lineHeight: 1 }}
            >
              {t("preview.permit_portal.tandc_2", { ns: "properties" })}
            </Typography>
          </Button>
          {` ${t("preview.permit_portal.tandc_3", { ns: "properties" })}`}
        </Typography>
      </Card>
      <Card
        sx={{
          borderRadius: 0,
          display: "flex",
          flexDirection: "column",
          backgroundColor: palette.primary.main,
          px: 1,
        }}
        raised
        elevation={3}
        component={"footer"}
      >
        <CardHeader
          title={publicContactInfo.name || ""}
          titleTypographyProps={{
            variant: "subtitle1",
            textAlign: "center",
            color: palette.primary.contrastText,
          }}
          sx={{
            borderBottom: `2px solid ${palette.primary.contrastText}`,
            mx: 4,
            pb: 2,
          }}
        />
        <CardContent>
          <Stack
            direction={"column"}
            spacing={3}
            display={"flex"}
            justifyContent={"center"}
            pl={2}
            pt={2}
          >
            <Stack
              direction={"row"}
              spacing={2}
              alignItems={"center"}
              flex={1}
              justifyContent={"left"}
            >
              <Business htmlColor={palette.primary.contrastText} />
              <Stack direction={"column"}>
                <Typography
                  variant={"caption"}
                  color={palette.primary.contrastText}
                >
                  {`${publicContactInfo?.location?.street}`}
                </Typography>
                <Typography
                  variant={"caption"}
                  color={palette.primary.contrastText}
                >
                  {`${publicContactInfo?.location?.city}, ${publicContactInfo?.location?.stateCode} ${publicContactInfo?.location?.postalCode}`}
                </Typography>
              </Stack>
            </Stack>
            <Stack
              direction={"row"}
              spacing={2}
              alignItems={"center"}
              flex={1}
              justifyContent={"left"}
            >
              <Phone htmlColor={palette.primary.contrastText} />
              <Typography
                variant={"caption"}
                color={palette.primary.contrastText}
              >
                {formatPhoneNumber(publicContactInfo?.primaryPhone || "")}
              </Typography>
            </Stack>
            <Stack
              direction={"row"}
              spacing={2}
              alignItems={"center"}
              flex={1}
              justifyContent={"left"}
            >
              <AlternateEmail htmlColor={palette.primary.contrastText} />
              <Typography
                variant={"caption"}
                color={palette.primary.contrastText}
              >
                {publicContactInfo?.primaryEmail || ""}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};
